import type { AppDescription, AppFeatures } from "common/components/SerializedApp";
import { getAppFeatures, getAppIconImage, getSourceMetadata } from "common/components/SerializedApp";
import type { AppUserAuthenticator, DataStore, MinimalAppEnvironment } from "common/components/types";
import type { TypeSchema } from "common/description";
import { isWireApp } from "common/is-wire-app";
import { getStaticAppConfig } from "common/static-app";
import { dontAwait } from "common/Support";
import { FirestoreDataStore } from "firestore-datastore/firestore-datastore";
import { getUserProfileTableInfo } from "generator/user-profile-info";
import { LocalPlaygroundDataStore } from "sharedUI/ai-playground/datastore";
import type { TableRelationData, Tables } from "sharedUI/ai-playground/interface";
import { WireAppEnvironment } from "webapp/hydrated-ui/app-environment";
import { ForwardingAppEnvironment } from "./forwarding-app-environment";
import { getBuilderAppDatabase, getPlayerDatabase, waitForFirestoreLibraries } from "./web-database";

interface AIAppData {
    tables: Tables | undefined;
    relColumns: TableRelationData | undefined;
}

export function makeAppEnvironmentForAppDescription(
    appDescription: AppDescription,
    schema: TypeSchema | undefined,
    authenticator: AppUserAuthenticator,
    appFeatures: AppFeatures,
    isBuilder: boolean,
    //optional parameters used by AI playground to generate the AI made apps on the client side
    { tables, relColumns }: AIAppData = { tables: undefined, relColumns: undefined }
): MinimalAppEnvironment {
    const { appID } = authenticator;
    const db = isBuilder ? getBuilderAppDatabase(appID) : getPlayerDatabase(appID);
    const isWire = isWireApp(getAppFeatures(appDescription));
    let dataStore: DataStore;
    if (tables !== undefined && schema !== undefined && relColumns !== undefined) {
        dataStore = new LocalPlaygroundDataStore(schema, tables, relColumns);
    } else {
        dataStore = new FirestoreDataStore(db, schema, authenticator, isBuilder, !isWire, isWire, undefined);
    }

    const staticAppConfig = getStaticAppConfig();
    if (staticAppConfig !== undefined) {
        const { publishedAppData: appData } = staticAppConfig;
        // Too many code paths assume we've loaded the Firestore libraries.
        // We have to wait for them before setting the schema.
        dontAwait(
            waitForFirestoreLibraries().then(() => {
                dataStore.setSchema(appData.schema);
            })
        );
    }

    const userProfileTableInfo = getUserProfileTableInfo(appDescription);
    const sourceMetadata = getSourceMetadata(appDescription);
    let appEnvironment: MinimalAppEnvironment;
    if (!isWire) {
        appEnvironment = new ForwardingAppEnvironment(
            authenticator,
            appFeatures,
            db,
            dataStore,
            isBuilder,
            sourceMetadata,
            userProfileTableInfo,
            getAppIconImage(appDescription)
        );
    } else {
        appEnvironment = new WireAppEnvironment(
            appFeatures,
            sourceMetadata,
            userProfileTableInfo,
            authenticator,
            db,
            dataStore,
            isBuilder
        );
    }
    dataStore.setAppEnvironment(appEnvironment);
    return appEnvironment;
}
